import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Country } from '../types/country';
import type { CustomerEligibility } from '../types/customerInfo';
import type { Localization } from '../types/localization';
import type { Session } from '../types/session';

export interface AppState {
  loading: boolean;
  session: Session | null;
  eligibility: CustomerEligibility | null;
  localization: Localization | null;
  countries: Country[] | null;
  readOnly: boolean;
  error: string | null;
}

const initialState: AppState = {
  loading: false,
  session: null,
  eligibility: null,
  localization: null,
  countries: [],
  readOnly: false,
  error: null,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setCountries(state, action: PayloadAction<Country[] | null>) {
      state.countries = action.payload;
    },
    setSession(state, action: PayloadAction<Session | null>) {
      state.session = action.payload;
    },
    setLocalization(state, action: PayloadAction<Localization | null>) {
      state.localization = action.payload;
    },
    setEligibility(state, action: PayloadAction<CustomerEligibility | null>) {
      state.eligibility = action.payload;
    },
    setReadonly(state, action: PayloadAction<boolean>) {
      state.readOnly = action.payload;
    },
    setError: {
      reducer(state, action: PayloadAction<string | null>) {
        state.error = action.payload;
      },
      prepare(
        error: string | null,
        cause?: unknown,
        context?: Record<string, unknown>,
      ) {
        return { payload: error, meta: { cause, context } };
      },
    },
  },
});

export const {
  setLoading,
  setSession,
  setEligibility,
  setLocalization,
  setCountries,
  setReadonly,
  setError,
} = appSlice.actions;

export default appSlice.reducer;
